import React from 'react';
import { useNavigate } from 'react-router-dom';
import EventPulsingBadge, { getEventBadgeState } from './EventPulsingBadge';
import { formatEventDateTime, parseEventDateValue } from '../utils/dateTime';

interface EventCardProps {
  id?: string;
  image: string;
  title: string;
  slug?: string;
  event_date: string; // YYYY-MM-DD
  is_active: boolean;
  is_reserved?: boolean;
  admins?: string[];
  created_at?: string;
  updated_at?: string;
  featured?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  id,
  image,
  title,
  event_date,
  is_active,
  is_reserved = false,
  featured = false,
}) => {
  const navigate = useNavigate();
  const isDisabled = !id;
  const handleOpenDetails = () => {
    if (!id) return;
    navigate(`/panel/eventos/${id}`);
  };
  const parsedEventDate = parseEventDateValue(event_date);
  const badgeState = getEventBadgeState(parsedEventDate ?? event_date);
  const formattedEventDate = formatEventDateTime(parsedEventDate);

  return (
    <button
      type="button"
      onClick={handleOpenDetails}
      className="panel-event-card group"
      aria-label={`Ver detalles del evento ${title}`}
      disabled={isDisabled}
    >
      <div className="panel-event-card__inner bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Image Container */}
        <div className="relative h-48 sm:h-56 bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top transition-transform duration-500 ease-out group-hover:scale-105"
            style={{ backgroundImage: `url(${image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          {badgeState.show && (
            <div className="absolute top-2 left-2 z-20">
              <EventPulsingBadge variant={badgeState.variant} label={badgeState.label} />
            </div>
          )}
          {featured && !badgeState.show && (
            <span className="absolute top-2 left-2 z-20 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
              Destacado
            </span>
          )}
          {(() => {
            let label = 'Inactivo';
            let cls = 'bg-slate-500';
            if (is_active) { label = 'Activo'; cls = 'bg-green-600 text-white'; }
            else if (is_reserved) { label = 'Reservado'; cls = 'bg-blue-400 text-white'; }
            return (
              <span className={`absolute top-2 right-2 z-20 text-xs font-semibold px-2 py-1 rounded ${cls}`}>
                {label}
              </span>
            );
          })()}
          <div className="absolute bottom-0 inset-x-0 z-20 p-4">
            <h3 className="text-white font-semibold text-lg leading-tight line-clamp-2">{title}</h3>
            <p className="text-white/80 text-sm">{formattedEventDate || 'Sin fecha definida'}</p>
          </div>
        </div>
      </div>
    </button>
  );
};

export default EventCard;
